type ApiPathLike = { table: { getStatisticsDataUrl: (id: string | number) => string } };
type BackendApiLike = { post: (url: string, data?: any, params?: any) => Promise<any> };
type TemporalDiffLike = { getPreciseDiff: (date1: any, date2: any) => any };
type MomentLike = { utc: (date: any, format?: string) => { toDate: () => Date } };

export class StatisticsVizBackendServiceTS {
  private readonly apiPath: ApiPathLike;
  private readonly backendApi: BackendApiLike;
  private readonly temporalDiff: TemporalDiffLike;
  private readonly moment: MomentLike;

  constructor(apiPath: ApiPathLike, backendApi: BackendApiLike, temporalDiff: TemporalDiffLike, moment: MomentLike) {
    this.apiPath = apiPath;
    this.backendApi = backendApi;
    this.temporalDiff = temporalDiff;
    this.moment = moment;
  }

  async fetchJson(tableId: string | number, chartIndex: number, requestParams: any) {
    const url = this.apiPath.table.getStatisticsDataUrl(tableId) + "?chart_index=" + chartIndex;
    return this.backendApi.post(url, {}, requestParams);
  }

  getRepresentation(resultData: any, config: any) {
    const statistics = resultData.values;

    const data: any[] = [];

    if (!statistics) {
      return data;
    }

    for (let i = 0, l = statistics.length; i < l; i++) {
      const originalStatistic = statistics[i];
      let chartCategory: any = {};
      for (let j = 0; j < config.chartCategory.length; j++) {
        if (config.chartCategory[j].fieldName === originalStatistic.field_name) {
          chartCategory = config.chartCategory[j];
          break;
        }
      }

      let formattedStatistic: any = {};
      if (originalStatistic) {
        if (chartCategory.type === "character") {
          formattedStatistic = {
            type: chartCategory.type,
            displayName: chartCategory.displayName,
            fieldName: originalStatistic.field_name,
            uniqueValues: originalStatistic.unique,
            blanks: originalStatistic.blanks,
          };
        } else if (chartCategory.type === "numeric") {
          formattedStatistic = {
            type: chartCategory.type,
            displayName: chartCategory.displayName,
            fieldName: originalStatistic.field_name,
            total: originalStatistic.total,
            average: originalStatistic.average,
            range: originalStatistic.range,
            stdDeviation: originalStatistic.std_dev,
            count: originalStatistic.count,
            positives: originalStatistic.positives,
            negatives: originalStatistic.negatives,
            zeros: originalStatistic.zeros,
            highest: [],
            lowest: [],
          };
          if (Array.isArray(originalStatistic.highest)) {
            for (let j = 0, m = originalStatistic.highest.length; j < m; j++) {
              formattedStatistic.highest.push({ value: originalStatistic.highest[j] });
            }
          } else {
            formattedStatistic.highest.push({ value: originalStatistic.highest });
          }
          if (Array.isArray(originalStatistic.lowest)) {
            for (let j = 0, m = originalStatistic.lowest.length; j < m; j++) {
              formattedStatistic.lowest.push({ value: originalStatistic.lowest[j] });
            }
          } else {
            formattedStatistic.lowest.push({ value: originalStatistic.lowest });
          }
        } else if (chartCategory.type === "date") {
          formattedStatistic = {
            type: chartCategory.type,
            displayName: chartCategory.displayName,
            fieldName: originalStatistic.field_name,
            average: this.getDateJSON(originalStatistic.average),
            range: originalStatistic.range,
            stdDeviation: originalStatistic.std_dev,
            count: originalStatistic.count,
            blanks: originalStatistic.blanks,
            highest: [],
            lowest: [],
          };
          if (Array.isArray(originalStatistic.highest)) {
            for (let j = 0, m = originalStatistic.highest.length; j < m; j++) {
              formattedStatistic.highest.push({ value: this.getDateJSON(originalStatistic.highest[j]) });
            }
          } else {
            formattedStatistic.highest.push({ value: this.getDateJSON(originalStatistic.highest) });
          }
          if (Array.isArray(originalStatistic.lowest)) {
            for (let j = 0, m = originalStatistic.lowest.length; j < m; j++) {
              formattedStatistic.lowest.push({ value: this.getDateJSON(originalStatistic.lowest[j]) });
            }
          } else {
            formattedStatistic.lowest.push({ value: this.getDateJSON(originalStatistic.lowest) });
          }
          if (formattedStatistic.lowest.length && formattedStatistic.highest.length) {
            formattedStatistic.formattedRange = this.temporalDiff.getPreciseDiff(
              formattedStatistic.highest[0].value,
              formattedStatistic.lowest[0].value
            );
          }
        } else if (chartCategory.type === "time") {
          formattedStatistic = {
            type: chartCategory.type,
            displayName: chartCategory.displayName,
            fieldName: originalStatistic.field_name,
            average: this.getTimeJSON(originalStatistic.average),
            range: originalStatistic.range,
            stdDeviation: originalStatistic.std_dev,
            count: originalStatistic.count,
            blanks: originalStatistic.blanks,
            highest: [],
            lowest: [],
          };
          if (Array.isArray(originalStatistic.highest)) {
            for (let j = 0, m = originalStatistic.highest.length; j < m; j++) {
              formattedStatistic.highest.push({ value: this.getTimeJSON(originalStatistic.highest[j]) });
            }
          } else {
            formattedStatistic.highest.push({ value: this.getTimeJSON(originalStatistic.highest) });
          }
          if (Array.isArray(originalStatistic.lowest)) {
            for (let j = 0, m = originalStatistic.lowest.length; j < m; j++) {
              formattedStatistic.lowest.push({ value: this.getTimeJSON(originalStatistic.lowest[j]) });
            }
          } else {
            formattedStatistic.lowest.push({ value: this.getTimeJSON(originalStatistic.lowest) });
          }

          formattedStatistic.formattedRange = this.temporalDiff.getPreciseDiff(
            "1900-01-01 " + formattedStatistic.highest[0].value,
            "1900-01-01 " + formattedStatistic.lowest[0].value
          );
        } else if (chartCategory.type === "datetime") {
          formattedStatistic = {
            type: chartCategory.type,
            displayName: chartCategory.displayName,
            fieldName: originalStatistic.field_name,
            average: this.getDateTimeJSON(originalStatistic.average),
            range: originalStatistic.range,
            stdDeviation: originalStatistic.std_dev,
            count: originalStatistic.count,
            blanks: originalStatistic.blanks,
            highest: [],
            lowest: [],
          };

          if (Array.isArray(originalStatistic.highest)) {
            for (let j = 0, m = originalStatistic.highest.length; j < m; j++) {
              formattedStatistic.highest.push({ value: this.getDateTimeJSON(originalStatistic.highest[j]) });
            }
          } else {
            formattedStatistic.highest.push({ value: this.getDateTimeJSON(originalStatistic.highest) });
          }
          if (Array.isArray(originalStatistic.lowest)) {
            for (let j = 0, m = originalStatistic.lowest.length; j < m; j++) {
              formattedStatistic.lowest.push({ value: this.getDateTimeJSON(originalStatistic.lowest[j]) });
            }
          } else {
            formattedStatistic.lowest.push({ value: this.getDateTimeJSON(originalStatistic.lowest) });
          }
          if (formattedStatistic.lowest.length && formattedStatistic.highest.length) {
            formattedStatistic.formattedRange = this.temporalDiff.getPreciseDiff(
              formattedStatistic.highest[0].value,
              formattedStatistic.lowest[0].value
            );
          }
        }
      }

      data.push(formattedStatistic);
    }
    return data;
  }

  private getDateJSON(date: any) {
    return this.moment.utc(date).toDate();
  }

  private getDateTimeJSON(dateTime: any) {
    return this.moment.utc(dateTime).toDate();
  }

  private getTimeJSON(time: any) {
    return time;
  }
}

export function createAngularCompatibleStatisticsVizService(
  apiPath: ApiPathLike,
  backendApi: BackendApiLike,
  temporalDiff: TemporalDiffLike,
  moment: MomentLike
) {
  const service = new StatisticsVizBackendServiceTS(apiPath, backendApi, temporalDiff, moment);
  return {
    fetchJson: service.fetchJson.bind(service),
    getRepresentation: service.getRepresentation.bind(service),
  };
}

